import type { ReactNode } from 'react';
import {
  ROLE_OWNERSHIPS,
  ROLE_OWNERSHIP_LABELS,
  ROLE_SCOPE_KINDS,
  ROLE_SCOPE_KIND_LABELS,
  type RoleAssignmentInput,
  type RoleOwnership,
  type RoleScopeKind,
} from '@nest-recruit/shared';
import { Field } from '../../../../components/Field';
import { MultiSelect, Select } from '../../../../components/Select';
import { useCanView } from '../../../../stores/session';
import { useRoles } from '../../api/roles-api';
import { useDepartments, useVentures } from '../../api/users-api';
import styles from './RoleReachFields.module.css';

/**
 * One role and its reach: the role, what it covers, and whether they see everything there or only their own.
 * F2.01 asks for both at invite time; the same four fields change the role of everyone ticked (USER-01's bulk
 * "Change role"). A role without a reach is not a permission, so the caller keeps its button off until
 * `isReachComplete` says so.
 */

export interface RoleReachDraft {
  readonly roleId: string | null;
  readonly scopeKind: RoleScopeKind;
  readonly ownership: RoleOwnership;
  readonly ventureIds: readonly string[];
  readonly departmentIds: readonly string[];
}

export const EMPTY_ROLE_REACH: RoleReachDraft = {
  roleId: null,
  scopeKind: 'venture',
  ownership: 'all',
  ventureIds: [],
  departmentIds: [],
};

export function isReachComplete(draft: RoleReachDraft): boolean {
  return (
    draft.roleId !== null &&
    (draft.scopeKind !== 'venture' || draft.ventureIds.length > 0) &&
    (draft.scopeKind !== 'department' || draft.departmentIds.length > 0)
  );
}

/** The request's `assignment`, once the draft is complete. */
export function toAssignment(draft: RoleReachDraft): RoleAssignmentInput | null {
  if (!isReachComplete(draft) || draft.roleId === null) return null;
  return {
    roleId: draft.roleId,
    scopeKind: draft.scopeKind,
    ownership: draft.ownership,
    ventureIds: draft.scopeKind === 'venture' ? [...draft.ventureIds] : [],
    departmentIds: draft.scopeKind === 'department' ? [...draft.departmentIds] : [],
  };
}

export function RoleReachFields({
  value,
  onChange,
}: {
  readonly value: RoleReachDraft;
  readonly onChange: (next: RoleReachDraft) => void;
}): ReactNode {
  // Review R22: `/roles` is asked for only by someone who may open People and roles.
  const roles = useRoles(useCanView('user_management'));
  const ventures = useVentures();
  const departments = useDepartments();

  return (
    <div className={styles.fields}>
      <Field label="Role" hint="What they will be able to do." isRequired>
        {({ inputId, describedBy }) => (
          <Select
            id={inputId}
            describedBy={describedBy}
            label="Role"
            placeholder="Choose a role"
            value={value.roleId}
            options={(roles.data ?? [])
              .filter((role) => role.key !== 'candidate')
              .map((role) => ({
                value: role.id,
                label: role.label,
                ...(role.description ? { description: role.description } : {}),
              }))}
            onChange={(roleId) => onChange({ ...value, roleId })}
          />
        )}
      </Field>

      <Field label="Covering" hint="A role without a reach is not a permission, so this can't be skipped." isRequired>
        {({ inputId, describedBy }) => (
          <Select
            id={inputId}
            describedBy={describedBy}
            label="Covering"
            value={value.scopeKind}
            options={ROLE_SCOPE_KINDS.map((kind) => ({ value: kind, label: ROLE_SCOPE_KIND_LABELS[kind] }))}
            onChange={(kind) => onChange({ ...value, scopeKind: kind as RoleScopeKind, ventureIds: [], departmentIds: [] })}
          />
        )}
      </Field>

      {value.scopeKind === 'venture' && (
        <Field label="Which ventures" isRequired>
          {({ inputId }) => (
            <MultiSelect
              id={inputId}
              label="Which ventures"
              placeholder="Choose ventures"
              value={[...value.ventureIds]}
              options={(ventures.data ?? []).map((venture) => ({
                value: venture.id,
                label: venture.name,
                description: venture.code,
              }))}
              onChange={(ventureIds) => onChange({ ...value, ventureIds: [...ventureIds] })}
            />
          )}
        </Field>
      )}

      {value.scopeKind === 'department' && (
        <Field label="Which departments" isRequired>
          {({ inputId }) => (
            <MultiSelect
              id={inputId}
              label="Which departments"
              placeholder="Choose departments"
              value={[...value.departmentIds]}
              options={(departments.data ?? []).map((department) => ({ value: department.id, label: department.name }))}
              onChange={(departmentIds) => onChange({ ...value, departmentIds: [...departmentIds] })}
            />
          )}
        </Field>
      )}

      <Field label="They can see">
        {({ inputId }) => (
          <Select
            id={inputId}
            label="They can see"
            value={value.ownership}
            options={ROLE_OWNERSHIPS.map((ownership) => ({ value: ownership, label: ROLE_OWNERSHIP_LABELS[ownership] }))}
            onChange={(ownership) => onChange({ ...value, ownership: ownership as RoleOwnership })}
          />
        )}
      </Field>
    </div>
  );
}
